<template>
  <v-layout align-center>
    <v-flex xs12 sm10 md10 lg8>
      <form v-scroll-to-error @submit.prevent="submitForm">
        <v-layout wrap>
          <v-flex xs12>
            <div v-if="$apollo.loading">
              <spinner />
            </div>
            <div v-else>
              <div class="title-screen">
                <span>Event info</span>
              </div>
              <event-info-form
                :vs="$v.form.generalInfo"
                :initial-values="form.generalInfo"
                :categories="categories"
                :show-buttons="false"
              />
              <counter-field
                v-if="extraModules.categoriesAndZones"
                v-model="form.generalInfo.numberOfZones"
                :min-value="1"
                :error-messages="{ between: 'This field should be between 1 and 10' }"
                label="Number of zones"
                :validator="$v.form.generalInfo.numberOfZones"
              />
              <div class="update-button">
                <main-button
                  text="Update"
                  depressed
                  :disabled="!isFormValid"
                  @click="$emit('submit')"
                />
              </div>
            </div>
          </v-flex>
        </v-layout>
      </form>
    </v-flex>
    <simple-snackbar :open.sync="snackbar" color="primary" message-prop="Event details saved" />
  </v-layout>
</template>

<script>
import { between, maxLength, minValue, required, requiredIf } from 'vuelidate/lib/validators'
import { date, httpUrl, maxDate, minDate } from './../../validators'

import { GET_EVENT_SETTINGS, UPDATE_EVENT } from '../../graphql/event.graphql'
import { GET_ALL_EVENT_CATEGORIES } from '../../graphql/eventCategory.graphql'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar'
import Spinner from '../shared/spinner'
import { submitGuard } from './../../utils'
import EventInfoForm from './event-info-form.vue'

export default {
  name: 'event-info-container',
  components: {
    SimpleSnackbar,
    Spinner,
    EventInfoForm,
  },
  props: {
    eventId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      snackbar: false,
      isSubcategoryRequiredField: false,
      extraModules: {},
      categories: [],
      form: {},
    }
  },
  validations() {
    return {
      form: {
        generalInfo: {
          title: { required, maxLength: maxLength(120) },
          eventCategoryId: { required },
          interests: {
            required: requiredIf(() => this.isSubcategoryRequiredField),
          },
          from: {
            required,
            date,
            maxDate: maxDate(() => new Date(this.form.generalInfo.to)),
          },
          to: {
            required,
            date,
            minDate: minDate(() => new Date(this.form.generalInfo.from)),
          },
          location: {
            venue: { required },
            address1: { required },
          },
          highlightedData: {
            ageRestriction: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.ageRestriction.enabled,
                ),
                minValue: minValue(0),
              },
            },
            freeEntranceWithAgeLimit: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.freeEntranceWithAgeLimit.enabled,
                ),
                minValue: minValue(0),
              },
            },
          },
          faqs: {
            $each: {
              answer: { required },
              question: { required },
            },
          },
          metadata: {
            webLink: {
              httpUrl,
            },
            facebookLink: {
              httpUrl,
            },
          },
        },
        numberOfZones: {
          required: requiredIf(({ modules }) => modules && modules.categoriesAndZones),
          between: between(1, 10),
        },
      },
    }
  },
  computed: {
    isFormValid() {
      return !this.$v.$error && !this.$v.$invalid
    },
  },
  watch: {
    'form.generalInfo.eventCategoryId': function () {
      const category = this.categories.find(c => c.id === this.form.generalInfo.eventCategoryId)
      if (!category) {
        return (this.isSubcategoryRequiredField = false)
      }

      return (this.isSubcategoryRequiredField = !!category.subCategories.length)
    },
  },
  methods: {
    async submitForm() {
      await submitGuard(this, async () => {
        const { metadata, location, interests, ...rest } = this.form.generalInfo

        await this.$apollo.mutate({
          mutation: UPDATE_EVENT,
          variables: {
            id: this.eventId,
            event: {
              ...rest,
              metadata,
              interests,
              eventCategoryId: this.form.generalInfo.eventCategoryId,
            },
            location,
          },
        })
        this.snackbar = true
      })
    },
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_SETTINGS,
      variables() {
        return {
          id: this.eventId,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && data) {
          const {
            title,
            cover,
            description,
            to,
            from,
            location,
            numberOfZones,
            extraModules,
            interests,
            eventCategory,
            organizerMessage,
            highlightedData,
            faqs,
            metadata,
          } = data.getEvent

          this.extraModules = extraModules
          this.form = {
            generalInfo: {
              title,
              from,
              to,
              location,
              numberOfZones,
              eventCategoryId: eventCategory.id,
              description,
              cover,
              organizerMessage,
              interests: interests.map(i => i.id),
              highlightedData,
              faqs,
              metadata: metadata ? metadata : { webLink: '', facebookLink: '' },
            },
            eventCategoryId: eventCategory.id,
          }
        }
      },
    },
    getAllEventCategories: {
      query: GET_ALL_EVENT_CATEGORIES,
      result({ data, loading }) {
        if (!loading && data) {
          this.categories = data.getAllEventCategories
        }
      },
    },
  },
}
</script>

<style scoped>
.table-layout {
  border-collapse: separate;
  width: 100%;
  border-spacing: 10px;
  display: table;
}

.table-cell-layout {
  display: table-cell;
}

.import-export-title {
  text-align: center;
}

@media (max-width: 800px) {
  .update-button button {
    width: 100%;
    padding: 12px 16px;
  }
}

.update-button {
  display: flex;
  justify-content: flex-end;
}

.v-btn {
  margin: 2rem 0 0 0 !important;
}

.category-section {
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

@media screen and (max-width: 800px) {
  .category-section {
    margin-top: 1.5rem;
  }
}
</style>
